<script setup lang="ts">
import { PageHeader, PageShell } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  canViewForecast,
  ForecastScenarioCreateForm,
  ForecastScenarioList,
  useForecastScenarios,
} from '@/features/forecast'
import { PlanningTabs } from '@/features/targets'
import { useSession } from '@/app/session'

const { t } = useI18n()
const session = useSession()

const canView = computed(() => canViewForecast(session.can))

const scenarios = useForecastScenarios({
  ledgerId: () => session.activeLedger.value?.id,
  enabled: () => canView.value,
})

const createErrorMessage = computed(() =>
  scenarios.hasCreateError.value
    ? (scenarios.createErrorDetail.value ?? t('forecast.createErrorBody'))
    : undefined,
)

function createScenario(input: Parameters<typeof scenarios.create>[0]): void {
  scenarios.create(input).catch(() => {
    // surfaced through scenarios.hasCreateError; nothing further to do here
  })
}

function deleteScenario(scenarioId: string): void {
  scenarios.remove(scenarioId).catch(() => {
    // surfaced through scenarios.hasDeleteError; nothing further to do here
  })
}
</script>

<template>
  <PageShell class="gap-6">
    <PageHeader :title="t('planning.title')">
      <template #subtitle>{{ t('planning.subtitle') }}</template>
    </PageHeader>
    <PlanningTabs />

    <div class="flex items-center justify-between">
      <h2 class="text-[length:var(--text-step-5)] font-semibold text-[color:var(--color-text)]">
        {{ t('forecast.title') }}
      </h2>
      <ForecastScenarioCreateForm
        v-if="canView && session.activeLedger.value !== undefined"
        :is-submitting="scenarios.isCreating.value"
        :error-message="createErrorMessage"
        @create="createScenario"
      />
    </div>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('forecast.noLedger') }}
    </p>

    <p v-else-if="!canView" class="text-[color:var(--color-text-muted)]">
      {{ t('forecast.noAccess') }}
    </p>

    <ForecastScenarioList
      v-else
      :scenarios="scenarios.scenarios.value"
      :is-loading="scenarios.isLoading.value"
      :error="scenarios.error.value"
      :retrying="scenarios.retrying.value"
      :deleting-id="scenarios.deletingId.value"
      @retry="scenarios.retry()"
      @delete="deleteScenario"
    />
  </PageShell>
</template>
